import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { Send, Menu, Crown, LogIn, Sparkles, ShoppingBasket } from "lucide-react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { BRAND, QUICK_ACTIONS, parseRecipe } from "@/constants/brand";
import { ChatMessage } from "@/components/ChatMessage";
import { CookingMode } from "@/components/CookingMode";
import { Sidebar } from "@/components/Sidebar";
import { PremiumModal } from "@/components/PremiumModal";
import { GroceryDrawer } from "@/components/GroceryDrawer";
import { addGroceryItems, useGrocery } from "@/lib/grocery";

const newSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const GREETING = {
  role: "assistant",
  text: "Ciao, tesoro! I'm Aunt Loretta. 🍅 Hungry? Not sure what to cook? Got a fridge full of odds and ends? Pull up a chair and tell me what's going on — or tap one of the buttons below and we'll figure out dinner together.",
  recipe: null,
};

export default function Chat() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(newSessionId());
  const [cookingRecipe, setCookingRecipe] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [groceryOpen, setGroceryOpen] = useState(false);
  const [savedTitles, setSavedTitles] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const { list: groceryList } = useGrocery();
  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const pendingModeRef = useRef("general");

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (params.get("premium") === "1" && user && !user.is_premium) setPremiumOpen(true);
  }, [params, user]);

  const send = async (text, modeOverride) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    const mode = modeOverride || pendingModeRef.current || "general";
    pendingModeRef.current = "general";
    setInput("");
    const history = messages
      .filter((m) => m.text)
      .map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.recipe ? `${m.text}` : m.text }));
    const userMsg = { role: "user", text: content, recipe: null };
    const next = [...messages, userMsg];
    setMessages([...next, { role: "assistant", text: "", recipe: null, streaming: true }]);
    setStreaming(true);

    const body = JSON.stringify({
      messages: [...history, { role: "user", content }],
      session_id: sessionId,
      mode,
    });
    const openStream = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const res = await fetch(`${API}/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body,
          });
          if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
          return res;
        } catch (err) {
          if (attempt >= 1) throw err;
          await new Promise((r) => setTimeout(r, 1200));
        }
      }
    };

    try {
      const res = await openStream();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let raw = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
        const { text, recipe } = parseRecipe(raw);
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", text: text || (recipe ? "" : raw), recipe, streaming: true };
          return copy;
        });
        scrollToBottom();
      }
      const { text, recipe } = parseRecipe(raw);
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", text, recipe, streaming: false };
        return copy;
      });
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", text: "Oh dear, my kitchen line dropped. Try me again in a moment, sweetheart.", recipe: null, streaming: false };
        return copy;
      });
    } finally {
      setStreaming(false);
      if (user && user.is_premium) setRefreshKey((k) => k + 1);
    }
  };

  const handleQuickAction = (action) => {
    const mode = action.id === "ask" ? "ask" : action.id === "fix" ? "fix" : "general";
    if (action.prompt.endsWith(": ") || action.prompt.endsWith(" ")) {
      pendingModeRef.current = mode;
      setInput(action.prompt);
      taRef.current?.focus();
    } else {
      send(action.prompt, mode);
    }
  };

  const saveFavorite = async (recipe) => {
    if (!user || !user.is_premium) {
      setPremiumOpen(true);
      return;
    }
    try {
      await api.post("/favorites", { title: recipe.title, recipe });
      setSavedTitles((s) => [...s, recipe.title]);
      setRefreshKey((k) => k + 1);
      toast.success(`Saved "${recipe.title}" to your favorites!`);
    } catch {
      toast.error("Couldn't save that one.");
    }
  };

  const addToGrocery = (recipe) => {
    const n = addGroceryItems(recipe.ingredients || []);
    toast.success(n > 0 ? `${n} ingredients added to your grocery list!` : "Already on your list, sugar.");
    setGroceryOpen(true);
  };

  const shareRecipe = async (recipe) => {
    try {
      const { data } = await api.post("/recipes/share", { title: recipe.title, recipe });
      const url = `${window.location.origin}/r/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard!");
      } catch {
        toast.success(url);
      }
    } catch {
      toast.error("Couldn't create a share link.");
    }
  };

  const suggestDinner = () => {
    setSidebarOpen(false);
    send("Loretta, based on my pantry and my tastes, what should I make for dinner tonight? Give me one full recipe.");
  };

  const loadConversation = async (sid) => {
    try {
      const { data } = await api.get(`/conversations/${sid}`);
      const rebuilt = data.messages.map((m) => {
        if (m.role === "assistant") {
          const { text, recipe } = parseRecipe(m.content);
          return { role: "assistant", text, recipe };
        }
        return { role: "user", text: m.content, recipe: null };
      });
      setMessages([GREETING, ...rebuilt]);
      setSessionId(sid);
      setSidebarOpen(false);
    } catch {
      toast.error("Couldn't load that conversation.");
    }
  };

  const startNew = () => {
    setMessages([GREETING]);
    setSessionId(newSessionId());
    setSidebarOpen(false);
  };

  const deepConvo = messages.length > 3;

  return (
    <div className="flex h-screen overflow-hidden bg-cream">
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onOpenPremium={() => setPremiumOpen(true)}
        onLoadConversation={loadConversation}
        onCookRecipe={(r) => setCookingRecipe(r)}
        onSuggestDinner={suggestDinner}
        refreshKey={refreshKey}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* header */}
        <header className="flex items-center justify-between border-b border-espresso/10 bg-white/70 px-4 py-3 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden" data-testid="open-sidebar-button">
              <Menu className="h-6 w-6 text-espresso" />
            </button>
            <div className="flex items-center gap-2">
              <img src={BRAND.logo} alt="Aunt Lorettas Kitchen Chat" className="h-10 w-10 rounded-full object-contain lg:hidden" />
              <div>
                <h1 className="font-serif text-lg font-bold leading-none text-espresso sm:text-xl">Aunt Lorettas Kitchen Chat</h1>
                <p className="text-xs tracking-wide text-loretta-red">{BRAND.tagline}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setGroceryOpen(true)}
              data-testid="grocery-button"
              title="Grocery list"
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-espresso/15 text-espressolt transition-colors hover:bg-creamalt hover:text-loretta-red"
            >
              <ShoppingBasket className="h-5 w-5" />
              {groceryList.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-loretta-red px-1 text-[11px] font-bold text-white" data-testid="grocery-count">
                  {groceryList.length}
                </span>
              )}
            </button>
            <button onClick={startNew} data-testid="new-chat-button" className="hidden rounded-full border border-espresso/15 px-4 py-2 text-sm text-espressolt transition-colors hover:bg-creamalt sm:block">New chat</button>
            {ready && !user && (
              <button onClick={() => navigate("/login")} data-testid="header-login-button" className="flex items-center gap-1.5 rounded-full border border-espresso/15 px-4 py-2 text-sm text-espresso transition-colors hover:bg-creamalt">
                <LogIn className="h-4 w-4" /> Sign in
              </button>
            )}
            {ready && user && !user.is_premium && (
              <button onClick={() => setPremiumOpen(true)} data-testid="header-premium-button" className="flex items-center gap-1.5 rounded-full bg-loretta-olive px-4 py-2 text-sm font-semibold text-cream transition-colors hover:bg-loretta-olivehover">
                <Crown className="h-4 w-4" /> Premium
              </button>
            )}
          </div>
        </header>

        {/* messages */}
        <div ref={scrollRef} className="chat-scroll flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          <div className="mx-auto flex max-w-3xl flex-col gap-5">
            {messages.map((m, i) => (
              <ChatMessage
                key={i}
                role={m.role}
                text={m.text}
                recipe={m.recipe}
                streaming={m.streaming}
                onStartCooking={(r) => setCookingRecipe(r)}
                onSave={m.role === "assistant" ? saveFavorite : undefined}
                saved={m.recipe && savedTitles.includes(m.recipe.title)}
                onAddToList={m.role === "assistant" ? addToGrocery : undefined}
                onShare={m.role === "assistant" ? shareRecipe : undefined}
              />
            ))}
          </div>
        </div>

        {/* composer */}
        <div className="border-t border-espresso/10 bg-white/70 px-4 py-4 backdrop-blur-xl sm:px-8">
          <div className="mx-auto max-w-3xl">
            {!deepConvo && (
              <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                {QUICK_ACTIONS.map((a, idx) => {
                  const Icon = Icons[a.icon] || Sparkles;
                  return (
                    <motion.button
                      key={a.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => handleQuickAction(a)}
                      data-testid={`quick-action-${a.id}`}
                      className="flex shrink-0 items-center gap-2 rounded-full border border-espresso/15 bg-white px-4 py-2 text-sm font-medium text-espresso transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-loretta-red hover:text-loretta-red"
                    >
                      <Icon className="h-4 w-4 text-loretta-red" /> {a.label}
                    </motion.button>
                  );
                })}
              </div>
            )}
            <div className="flex items-end gap-2 rounded-3xl border border-espresso/15 bg-white p-2 shadow-[0_4px_20px_rgba(44,30,22,0.05)]">
              <textarea
                ref={taRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Ask Aunt Loretta anything… what should we cook today?"
                data-testid="chat-input"
                className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] text-espresso outline-none placeholder:text-espresso/40"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="send-button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-loretta-red text-white transition-colors hover:bg-loretta-redhover disabled:opacity-40"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
            {ready && !user && (
              <p className="mt-2 text-center text-xs text-espresso/40">Free & unlimited — Loretta won't remember between visits unless you go Premium.</p>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {cookingRecipe && <CookingMode recipe={cookingRecipe} onClose={() => setCookingRecipe(null)} />}
      </AnimatePresence>
      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />
      <GroceryDrawer open={groceryOpen} onClose={() => setGroceryOpen(false)} />
    </div>
  );
}
